/**
 * A single sortable step within the approval chain builder.
 *
 * Owns the drag handle + the per-step fields (name, approvers, and the approval
 * threshold with its conditional count, in `StepThresholdFields`). All state is
 * read/written through the parent form's `react-hook-form` context via
 * `FormField` / `Controller`, so validation + error surfacing come for free.
 */
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Card,
  CardContent,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  TooltipProvider,
} from '@monksflow/monks-ui';
import clsx from 'clsx';
import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import type { ApprovalChainAssigneeOption, ApprovalChainFormInput } from '../../model/schema';
import { ApproverPicker, type StepRoleCatalogue } from '../ApproverPicker';
import { ApprovalChainStepRowHeader } from './ApprovalChainStepRowHeader';
import { StepThresholdFields } from './StepThresholdFields';
import { useTranslation } from 'react-i18next';

export interface ApprovalChainStepRowProps {
  /** Stable client-side key — the dnd id AND the react-hook-form array key. */
  tempId: string;
  /** Zero-based position in the steps array (drives the "Step N" label). */
  index: number;
  /** Project members the assignee picker offers before any search. */
  members: ApprovalChainAssigneeOption[];
  /** True while the project roster is still loading (disables the picker). */
  isLoadingMembers: boolean;
  /** Workspace id used to search workspace-level users server-side. */
  workspaceId: string | null | undefined;
  /** The roles the step may name, shared by every row. */
  stepRoles: StepRoleCatalogue;
  canRemove: boolean;
  /** False on the first step — nothing above it to swap with. */
  canMoveUp: boolean;
  /** False on the last step. */
  canMoveDown: boolean;
  /** Index of the last step, so a row can tell when a move lands it at an end. */
  lastIndex: number;
  disabled: boolean;
  onRemove: (index: number) => void;
  onMove: (from: number, to: number) => void;
}

export function ApprovalChainStepRow(props: ApprovalChainStepRowProps) {
  const { t } = useTranslation('approvalChains');

  const { index, canRemove, canMoveUp, canMoveDown, lastIndex, disabled } = props;
  const { control } = useFormContext<ApprovalChainFormInput>();
  useRecheckApproversTogether(index);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props.tempId,
    disabled,
  });

  const rowStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Card
      ref={setNodeRef}
      style={rowStyle}
      compact
      variant="outline"
      data-testid="approval-chain-step-row"
      data-temp-id={props.tempId}
      className={clsx('shadow-none', isDragging && 'opacity-50')}
    >
      <CardContent className="flex flex-col gap-4">
        <TooltipProvider>
          <ApprovalChainStepRowHeader
            index={index}
            lastIndex={lastIndex}
            canMoveUp={canMoveUp}
            canMoveDown={canMoveDown}
            canRemove={canRemove}
            disabled={disabled}
            dragAttributes={attributes}
            dragListeners={listeners}
            onMove={props.onMove}
            onRemove={props.onRemove}
          />

          <FormField
            control={control}
            name={`steps.${index}.name`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t('manageApprovalChains.approvalChainStepRow.stepName')}
                  <span aria-hidden="true" className="text-destructive">
                    {' '}
                    *
                  </span>
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    size="lg"
                    disabled={disabled}
                    placeholder={t('manageApprovalChains.approvalChainStepRow.eGLegalReview')}
                    data-testid="approval-chain-step-name"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <ApproverPicker
            index={index}
            members={props.members}
            isLoadingMembers={props.isLoadingMembers}
            workspaceId={props.workspaceId}
            stepRoles={props.stepRoles}
            disabled={disabled}
          />

          <StepThresholdFields index={index} disabled={disabled} />
        </TooltipProvider>
      </CardContent>
    </Card>
  );
}

/**
 * A step's approvers and threshold are checked together, but the form re-checks only the field
 * that changed. Once the form has been submitted, or any of them shows an error, any change to
 * one re-checks them all, so an error never lingers under a field the user did not touch.
 */
function useRecheckApproversTogether(index: number) {
  const { control, trigger, getFieldState, formState } = useFormContext<ApprovalChainFormInput>();
  const { isSubmitted } = formState;
  const approvers = useWatch({
    control,
    name: [
      `steps.${index}.approverRole`,
      `steps.${index}.assignees`,
      `steps.${index}.approvalThresholdType`,
      `steps.${index}.approvalThresholdCount`,
    ],
  });
  const approversKey = JSON.stringify(approvers);

  // Keyed on approversKey, since the watched values are new arrays on every render.
  useEffect(() => {
    const fields = [
      `steps.${index}.assignees`,
      `steps.${index}.approvalThresholdType`,
      `steps.${index}.approvalThresholdCount`,
    ] as const;
    const showsError = fields.some((name) => getFieldState(name).error);
    if (isSubmitted || showsError) void trigger([...fields]);
  }, [approversKey, index, isSubmitted, trigger, getFieldState]);
}
